import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { hrApi } from '@/api';
import { EmployeeSelect, EmploymentTypeSelect } from '@/components/selects';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import { StatCard } from '@/components/ui/StatCard';
import { TimePicker } from '@/components/ui/TimePicker';
import { ViewModeToggle, type ViewMode } from '@/components/ui/ViewModeToggle';
import { HrMonthCalendarGrid } from '@/modules/hr/components/HrMonthCalendarGrid';
import {
  HR_DAY_TYPE_STYLES,
  hrDateKey,
  hrDayTypeBackground,
  hrFormatHours,
  hrFormatMinutes,
  hrMonthRange,
} from '@/modules/hr/components/hrCalendarUtils';
import { HrScheduleCalendarLegend } from '@/modules/hr/components/HrScheduleCalendarLegend';
import { HrWeekPatternsSection } from '@/modules/hr/components/HrWeekPatternsSection';
import { useEmployeeScheduleCalendar } from '@/modules/hr/hooks/useEmployeeScheduleCalendar';
import { cn } from '@/utils/cn';
import type {
  HrEmploymentHistory,
  HrEmploymentType,
  HrExpectedHours,
  HrHrWorkforceDashboard,
  HrMonthlyAttendanceSummary,
  HrWeeklyOvertimeSummary,
  HrWorkCalendarOverride,
} from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const DAY_TYPES = [
  { value: 'WORKING', label: 'Working day' },
  { value: 'WEEKEND', label: 'Weekend' },
  { value: 'HOLIDAY', label: 'Holiday' },
  { value: 'HALF_DAY', label: 'Half day' },
  { value: 'SPECIAL', label: 'Special day' },
];

export function HrWorkforcePage() {
  const [searchParams] = useSearchParams();
  const [types, setTypes] = useState<HrEmploymentType[]>([]);
  const [hrDash, setHrDash] = useState<HrHrWorkforceDashboard | null>(null);
  const [employeeId, setEmployeeId] = useState(() => searchParams.get('employeeId') ?? '');
  const [history, setHistory] = useState<HrEmploymentHistory[]>([]);
  const [expected, setExpected] = useState<HrExpectedHours | null>(null);
  const [summary, setSummary] = useState<HrMonthlyAttendanceSummary | null>(null);
  const [overrides, setOverrides] = useState<HrWorkCalendarOverride[]>([]);
  const [weeklyOt, setWeeklyOt] = useState<HrWeeklyOvertimeSummary[]>([]);
  const [changeType, setChangeType] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  const [overrideViewMode, setOverrideViewMode] = useState<ViewMode>('calendar');
  const [overrideViewDate, setOverrideViewDate] = useState(() => new Date());
  const [overrideDate, setOverrideDate] = useState('');
  const [overrideDayType, setOverrideDayType] = useState('WORKING');
  const [overrideStart, setOverrideStart] = useState('');
  const [overrideEnd, setOverrideEnd] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  const overridesByDay = useMemo(() => {
    const map = new Map<string, HrWorkCalendarOverride>();
    for (const o of overrides) {
      map.set(o.calendarDate.slice(0, 10), o);
    }
    return map;
  }, [overrides]);

  const calendarEmployeeIds = useMemo(
    () => (employeeId ? [Number(employeeId)] : []),
    [employeeId],
  );

  const workforceCalendar = useEmployeeScheduleCalendar({
    viewDate: overrideViewDate,
    employeeIds: calendarEmployeeIds,
    enabled: Boolean(employeeId) && overrideViewMode === 'calendar',
  });

  const load = useCallback(() => {
    hrApi.listEmploymentTypes().then(setTypes).catch((e) => showApiError(e, 'Failed to load employment types'));
    hrApi.hrWorkforceDashboard().then(setHrDash).catch((e) => showApiError(e, 'Failed to load HR dashboard'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fromQuery = searchParams.get('employeeId') ?? '';
    if (fromQuery) {
      setEmployeeId(fromQuery);
    }
  }, [searchParams]);

  const loadEmployee = useCallback(async (id: number) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const start = `${y}-${String(m).padStart(2, '0')}-01`;
    const end = new Date(y, m, 0).toISOString().slice(0, 10);
    const [h, e, s, o, w] = await Promise.all([
      hrApi.employmentHistory(id),
      hrApi.expectedHours(id, start, end),
      hrApi.monthlyAttendanceSummary(id, y, m),
      hrApi.listCalendarOverrides(id, start, end),
      hrApi.listWeeklyOvertime(id, start, end),
    ]);
    setHistory(h);
    setExpected(e);
    setSummary(s);
    setOverrides(o);
    setWeeklyOt(w);
  }, []);

  useEffect(() => {
    if (!employeeId) return;
    loadEmployee(Number(employeeId)).catch((e) => showApiError(e, 'Failed to load employee workforce data'));
  }, [employeeId, loadEmployee]);

  const changeEmployment = async () => {
    if (!employeeId || !effectiveDate) return;
    try {
      await hrApi.changeEmploymentType({
        employeeId: Number(employeeId),
        employmentTypeCode: changeType,
        effectiveDate,
        endDate: endDate || undefined,
        reason: reason || undefined,
      });
      notify.success('Employment type changed');
      await loadEmployee(Number(employeeId));
      load();
    } catch (e) {
      showApiError(e, 'Failed to change employment type');
    }
  };

  const runMonthEnd = async () => {
    const now = new Date();
    try {
      await hrApi.runMonthEnd(now.getFullYear(), now.getMonth() + 1);
      notify.success('Month-end workforce process completed');
      load();
      if (employeeId) await loadEmployee(Number(employeeId));
    } catch (e) {
      showApiError(e, 'Month-end failed');
    }
  };

  const saveOverride = async () => {
    if (!employeeId || !overrideDate) return;
    try {
      await hrApi.upsertCalendarOverride({
        employeeId: Number(employeeId),
        calendarDate: overrideDate,
        dayType: overrideDayType,
        startTime: overrideStart || undefined,
        endTime: overrideEnd || undefined,
        isHalfDay: overrideDayType === 'HALF_DAY',
        reason: overrideReason || undefined,
      });
      notify.success('Calendar override saved');
      setOverrides(await hrApi.listCalendarOverrides(Number(employeeId)));
      if (employeeId) await loadEmployee(Number(employeeId));
    } catch (e) {
      showApiError(e, 'Failed to save calendar override');
    }
  };

  const removeOverride = async (date: string) => {
    if (!employeeId) return;
    try {
      await hrApi.deleteCalendarOverride(Number(employeeId), date);
      notify.success('Override removed');
      setOverrides(await hrApi.listCalendarOverrides(Number(employeeId)));
      if (employeeId) await loadEmployee(Number(employeeId));
    } catch (e) {
      showApiError(e, 'Failed to remove override');
    }
  };

  const selectOverrideDay = (dayKey: string) => {
    setOverrideDate(dayKey);
    const existing = overridesByDay.get(dayKey);
    if (existing) {
      setOverrideDayType(existing.dayType);
      setOverrideStart(existing.startTime ?? '');
      setOverrideEnd(existing.endTime ?? '');
      setOverrideReason(existing.reason ?? '');
    }
  };

  const reloadOverridesForMonth = useCallback(async () => {
    if (!employeeId) return;
    const { start, end } = hrMonthRange(overrideViewDate);
    setOverrides(await hrApi.listCalendarOverrides(Number(employeeId), start, end));
  }, [employeeId, overrideViewDate]);

  useEffect(() => {
    if (employeeId && overrideViewMode === 'calendar') {
      reloadOverridesForMonth().catch((e) => showApiError(e, 'Failed to load overrides'));
    }
  }, [employeeId, overrideViewMode, overrideViewDate, reloadOverridesForMonth]);

  const aggregateWeekly = async () => {
    if (!employeeId) return;
    try {
      await hrApi.aggregateWeeklyOvertime(Number(employeeId));
      notify.success('Weekly overtime aggregated');
      await loadEmployee(Number(employeeId));
    } catch (e) {
      showApiError(e, 'Weekly OT aggregation failed');
    }
  };

  const aggregateCompanyWeekly = async () => {
    try {
      const res = await hrApi.aggregateCompanyWeeklyOvertime();
      notify.success(`Weekly OT aggregated for ${res.employeesProcessed} employees`);
      load();
    } catch (e) {
      showApiError(e, 'Company weekly OT failed');
    }
  };

  const confirmProbations = async () => {
    try {
      const res = await hrApi.confirmDueProbations();
      notify.success(`${res.confirmed} employee(s) confirmed from probation`);
    } catch (e) {
      showApiError(e, 'Probation confirmation failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Workforce Foundation"
        description="Employment types, calendar overrides, weekly overtime, schedules, and month-end payroll inputs"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Workforce' }]}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total employees" value={hrDash?.totalEmployees ?? '-'} />
        <StatCard title="Attendance compliance %" value={hrDash?.attendanceCompliancePercent ?? '-'} />
        <StatCard title="Overtime (hrs)" value={hrDash ? hrFormatMinutes(hrDash.totalOvertimeMinutes) : '—'} />
        <StatCard title="Payroll ready" value={hrDash?.payrollReadyCount ?? '-'} />
      </div>

      <div className="mb-6 card p-4">
        <h3 className="mb-1 font-semibold">Week pattern master</h3>
        <p className="mb-4 text-sm text-muted">
          Define reusable working-week templates for the active company. Assign them under Org → Companies → Calendar.
        </p>
        <HrWeekPatternsSection compact />
      </div>

      <div className="mb-6 card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">Operations</h3>
          <PageActionGate action="create">
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={confirmProbations}>
                Confirm due probations
              </Button>
              <Button variant="secondary" onClick={aggregateCompanyWeekly}>
                Aggregate weekly OT (company)
              </Button>
              <Button variant="secondary" onClick={runMonthEnd}>
                Run month-end
              </Button>
            </div>
          </PageActionGate>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {types.map((t) => (
            <div key={t.id} className="rounded border p-3 text-sm">
              <div className="font-medium">{t.name}</div>
              <div className="text-muted">{t.code}</div>
              <div className="text-muted">
                {t.payrollMode}
                {t.requiresEndDate ? ' · end date required' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-4">
        <h3 className="mb-3 font-semibold">Employee workforce</h3>
        <EmployeeSelect value={employeeId} onChange={setEmployeeId} className="mb-4 max-w-md" hrScoped />

        {summary && expected && (
          <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard title="Expected hrs" value={hrFormatMinutes(summary.expectedMinutes)} />
            <StatCard title="Worked hrs" value={hrFormatMinutes(summary.workedMinutes)} />
            <StatCard title="Missing hrs" value={hrFormatMinutes(summary.missingMinutes)} />
            <StatCard title="Daily OT hrs" value={hrFormatMinutes(summary.dailyOvertimeMinutes ?? 0)} />
            <StatCard title="Weekly OT hrs" value={hrFormatMinutes(summary.weeklyOvertimeMinutes ?? 0)} />
          </div>
        )}

        {expected && (
          <p className="mb-4 text-sm text-muted">
            Schedule: {expected.shiftName ?? 'Default'} · {expected.workingDays} working days ·{' '}
            {hrFormatHours(expected.expectedDailyHours)}/day · {hrFormatHours(expected.expectedMonthlyHours)}/month
          </p>
        )}

        <div className="mb-6 rounded border p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="font-medium">Work calendar overrides</h4>
              <span className="text-xs text-muted">
                Inherits company calendar and{' '}
                <Link to="/hr/holidays" className="text-primary-600 underline dark:text-primary-400">
                  public holidays
                </Link>
                ; override per date when needed
              </span>
            </div>
            <ViewModeToggle value={overrideViewMode} onChange={setOverrideViewMode} />
          </div>
          <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <DatePicker label="Date" value={overrideDate} onChange={setOverrideDate} />
            <Select label="Day type" value={overrideDayType} onChange={setOverrideDayType} options={DAY_TYPES} />
            <Input label="Reason" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
            <TimePicker label="Start (optional)" value={overrideStart} onChange={setOverrideStart} />
            <TimePicker label="End (optional)" value={overrideEnd} onChange={setOverrideEnd} />
          </div>
          <PageActionGate action="create">
            <Button onClick={saveOverride} disabled={!employeeId || !overrideDate}>
              Save override
            </Button>
          </PageActionGate>

          {!employeeId ? (
            <p className="mt-4 text-sm text-muted">Select an employee to view or edit calendar overrides.</p>
          ) : overrideViewMode === 'calendar' ? (
            <div className="mt-4 space-y-3">
              <HrScheduleCalendarLegend />
              <HrMonthCalendarGrid
                viewDate={overrideViewDate}
                onViewDateChange={setOverrideViewDate}
                onDayClick={(day) => selectOverrideDay(hrDateKey(day))}
                loading={workforceCalendar.loading}
                getDayCellClassName={(day) => {
                  const resolved = employeeId
                    ? workforceCalendar.getResolvedDay(Number(employeeId), day)
                    : undefined;
                  if (!resolved) return undefined;
                  return cn('border-slate-200 dark:border-slate-700', hrDayTypeBackground(resolved.dayType));
                }}
                getDayTitle={(day) =>
                  employeeId ? workforceCalendar.getDayTooltip(Number(employeeId), day) : undefined
                }
                renderDay={(day) => {
                  const key = hrDateKey(day);
                  const resolved = employeeId
                    ? workforceCalendar.getResolvedDay(Number(employeeId), day)
                    : undefined;
                  const o = overridesByDay.get(key);
                  const label = o?.dayType ?? resolved?.dayType ?? 'WORKING';
                  const detail = o?.reason ?? resolved?.holidayName;
                  return (
                    <div
                      className={cn(
                        'rounded px-1 py-0.5 font-medium',
                        HR_DAY_TYPE_STYLES[label] ?? HR_DAY_TYPE_STYLES.WORKING,
                      )}
                    >
                      {label}
                      {detail ? <div className="truncate font-normal opacity-80">{detail}</div> : null}
                    </div>
                  );
                }}
              />
            </div>
          ) : overrides.length > 0 ? (
            <div className="mt-4 space-y-2">
              {overrides.map((o) => (
                <div key={o.id} className="flex items-center justify-between rounded border p-2 text-sm">
                  <span>
                    {o.calendarDate} · {o.dayType}
                    {o.startTime ? ` · ${o.startTime}-${o.endTime ?? '?'}` : ''}
                    {o.reason ? ` — ${o.reason}` : ''}
                  </span>
                  <PageActionGate action="delete">
                    <Button variant="secondary" size="sm" onClick={() => void removeOverride(o.calendarDate)}>
                      Remove
                    </Button>
                  </PageActionGate>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted">No overrides for this period.</p>
          )}
        </div>

        <div className="mb-6 rounded border p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium">Weekly overtime</h4>
            <PageActionGate action="create">
              <Button variant="secondary" size="sm" onClick={aggregateWeekly} disabled={!employeeId}>
                Recalculate current week
              </Button>
            </PageActionGate>
          </div>
          {weeklyOt.length === 0 ? (
            <p className="text-sm text-muted">No weekly summaries yet. Run aggregation or wait for Sunday scheduler.</p>
          ) : (
            <div className="space-y-2">
              {weeklyOt.map((w) => (
                <div key={w.id} className="rounded border p-3 text-sm">
                  <div className="font-medium">
                    {w.weekStartDate} → {w.weekEndDate}
                  </div>
                  <div className="text-muted">
                    Worked {hrFormatMinutes(w.workedMinutes)} · Threshold {hrFormatMinutes(w.weeklyThresholdMinutes)}
                  </div>
                  <div>
                    Daily OT {hrFormatMinutes(w.dailyOvertimeMinutes)} · Weekly OT{' '}
                    {hrFormatMinutes(w.weeklyOvertimeMinutes)} · Billable{' '}
                    {hrFormatMinutes(w.billableOvertimeMinutes)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <EmploymentTypeSelect label="New type" value={changeType} onChange={setChangeType} required />
          <DatePicker label="Effective date" value={effectiveDate} onChange={setEffectiveDate} />
          <DatePicker label="End date (contract/intern)" value={endDate} onChange={setEndDate} />
          <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <PageActionGate action="create">
          <Button onClick={changeEmployment} disabled={!employeeId || !changeType || !effectiveDate}>
            Change employment type
          </Button>
        </PageActionGate>

        {history.length > 0 && (
          <div className="mt-6">
            <h4 className="mb-2 font-medium">Employment history</h4>
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} className="rounded border p-3 text-sm">
                  <div className="font-medium">
                    {h.startDate}
                    {h.endDate ? ` → ${h.endDate}` : ' → present'} · {h.employmentTypeCode}
                  </div>
                  {h.previousTypeCode && <div className="text-muted">From {h.previousTypeCode}</div>}
                  {h.reason && <div>{h.reason}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
